import { createMutation, type QueryClient } from '@tanstack/svelte-query'
import camelcaseKeys from 'camelcase-keys'
import { get } from 'svelte/store'

import { loggedInStore } from '@stores/logged-in-store'
import { handleFetch } from '@utils/fetch'
import { queryClient } from '@utils/svelte-query-client'

interface HuggingFaceToken {
  id: number
  name: string
  tokenPreview: string
  organizationId: number
  creatorId: number
  creatorName: string
  createdAt: string
  updatedAt: string
}

interface SaveHuggingFaceTokenInput {
  shouldSave: boolean
  token: string | null | undefined
  tokenName: string
}

interface CreateHuggingFaceTokenInput {
  name: string
  token: string
}

interface UpdateHuggingFaceTokenInput {
  id: number
  name?: string
  token?: string
}

interface HuggingFaceTokenMutationUser {
  creatorId: number
  creatorName: string
  organizationId: number
}

interface HuggingFaceTokensMutationContext {
  previousTokens?: HuggingFaceToken[]
}

interface SaveHuggingFaceTokenMutation {
  mutateAsync: (input: CreateHuggingFaceTokenInput) => Promise<number>
}

interface PersistSavedHuggingFaceTokenInput extends SaveHuggingFaceTokenInput {
  mutation: SaveHuggingFaceTokenMutation
  onSuccess?: (createdTokenId: number) => void
}

const huggingFaceTokensQueryKey = ['huggingface-token', 'list'] as const

const fetchHuggingFaceTokens = async () => {
  const rawTokens = camelcaseKeys(await (await handleFetch('/huggingface-token/')).json(), {
    deep: true,
  })
  return rawTokens as HuggingFaceToken[]
}

const huggingFaceTokensQuery = () => ({
  queryKey: huggingFaceTokensQueryKey,
  queryFn: fetchHuggingFaceTokens,
})

const loadHuggingFaceTokens = async () => {
  return queryClient.fetchQuery(huggingFaceTokensQuery())
}

const refreshHuggingFaceTokens = async (activeQueryClient: QueryClient = queryClient) => {
  await activeQueryClient.invalidateQueries({ queryKey: huggingFaceTokensQueryKey })
  return activeQueryClient.fetchQuery(huggingFaceTokensQuery())
}

const getHuggingFaceTokenPreview = (token: string) => {
  const tokenTail = token.slice(-4)
  return `hf_...${tokenTail}`
}

const createHuggingFaceToken = async ({ name, token }: CreateHuggingFaceTokenInput) => {
  const createResponse = await handleFetch('/huggingface-token/', {
    method: 'POST',
    body: {
      name,
      token,
    },
  })
  const createResponseBody = camelcaseKeys(await createResponse.json(), { deep: true }) as {
    id: number
  }
  return createResponseBody.id
}

const updateHuggingFaceToken = async ({ id, name, token }: UpdateHuggingFaceTokenInput) => {
  const updateResponse = await handleFetch(`/huggingface-token/${id}`, {
    method: 'PUT',
    body: {
      ...(name ? { name } : {}),
      ...(token ? { token } : {}),
    },
  })
  const updatedToken = camelcaseKeys(await updateResponse.json(), {
    deep: true,
  }) as HuggingFaceToken
  return updatedToken
}

const deleteHuggingFaceToken = async (id: number) => {
  await handleFetch(`/huggingface-token/${id}`, {
    method: 'DELETE',
  })
}

const buildSaveHuggingFaceTokenInput = ({
  shouldSave,
  token,
  tokenName,
}: SaveHuggingFaceTokenInput) => {
  if (!shouldSave) {
    return undefined
  }

  const trimmedToken = token?.trim()
  if (!trimmedToken) {
    throw new Error('A custom HuggingFace token is required to save it.')
  }

  const trimmedTokenName = tokenName.trim()
  if (!trimmedTokenName) {
    throw new Error('Token name is required when saving a HuggingFace token.')
  }

  return {
    name: trimmedTokenName,
    token: trimmedToken,
  } satisfies CreateHuggingFaceTokenInput
}

const saveHuggingFaceTokenIfNeeded = async ({
  shouldSave,
  token,
  tokenName,
  mutation,
}: SaveHuggingFaceTokenInput & { mutation: SaveHuggingFaceTokenMutation }) => {
  const mutationInput = buildSaveHuggingFaceTokenInput({
    shouldSave,
    token,
    tokenName,
  })
  if (!mutationInput) {
    return undefined
  }
  return mutation.mutateAsync(mutationInput)
}

const persistSavedHuggingFaceToken = async ({
  shouldSave,
  token,
  tokenName,
  mutation,
  onSuccess,
}: PersistSavedHuggingFaceTokenInput) => {
  const createdTokenId = await saveHuggingFaceTokenIfNeeded({
    shouldSave,
    token,
    tokenName,
    mutation,
  })
  if (createdTokenId === undefined) {
    return undefined
  }
  onSuccess?.(createdTokenId)
  return createdTokenId
}

const getCurrentHuggingFaceMutationUser = (): HuggingFaceTokenMutationUser => {
  const currentUser = get(loggedInStore)
  return {
    creatorId: currentUser.userId,
    creatorName: currentUser.fullName,
    organizationId: currentUser.primaryOrganizationId,
  }
}

const createCreateHuggingFaceTokenMutationOptions = (
  activeQueryClient: QueryClient,
  getCurrentUser: () => HuggingFaceTokenMutationUser = getCurrentHuggingFaceMutationUser,
) => ({
  mutationFn: createHuggingFaceToken,
  onMutate: async ({
    name,
    token,
  }: CreateHuggingFaceTokenInput): Promise<HuggingFaceTokensMutationContext> => {
    await activeQueryClient.cancelQueries({ queryKey: huggingFaceTokensQueryKey })
    const previousTokens =
      activeQueryClient.getQueryData<HuggingFaceToken[]>(huggingFaceTokensQueryKey)
    const currentUser = getCurrentUser()
    const optimisticToken: HuggingFaceToken = {
      id: -Date.now(),
      name,
      tokenPreview: getHuggingFaceTokenPreview(token),
      organizationId: currentUser.organizationId,
      creatorId: currentUser.creatorId,
      creatorName: currentUser.creatorName,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    activeQueryClient.setQueryData<HuggingFaceToken[]>(huggingFaceTokensQueryKey, [
      optimisticToken,
      ...(previousTokens ?? []),
    ])
    return { previousTokens }
  },
  onError: (
    _error: unknown,
    _variables: CreateHuggingFaceTokenInput,
    context?: HuggingFaceTokensMutationContext,
  ) => {
    if (context?.previousTokens) {
      activeQueryClient.setQueryData(huggingFaceTokensQueryKey, context.previousTokens)
    }
  },
  onSettled: async () => {
    await refreshHuggingFaceTokens(activeQueryClient)
  },
})

const createSaveHuggingFaceTokenMutation = (activeQueryClient: QueryClient) => {
  return createMutation(createCreateHuggingFaceTokenMutationOptions(activeQueryClient))
}

const createUpdateHuggingFaceTokenMutationOptions = (activeQueryClient: QueryClient) => ({
  mutationFn: updateHuggingFaceToken,
  onMutate: async ({
    id,
    name,
  }: UpdateHuggingFaceTokenInput): Promise<HuggingFaceTokensMutationContext> => {
    await activeQueryClient.cancelQueries({ queryKey: huggingFaceTokensQueryKey })
    const previousTokens =
      activeQueryClient.getQueryData<HuggingFaceToken[]>(huggingFaceTokensQueryKey)
    activeQueryClient.setQueryData<HuggingFaceToken[]>(
      huggingFaceTokensQueryKey,
      (previousTokens ?? []).map((savedToken) =>
        savedToken.id === id
          ? {
              ...savedToken,
              ...(name ? { name } : {}),
              updatedAt: new Date().toISOString(),
            }
          : savedToken,
      ),
    )
    return { previousTokens }
  },
  onError: (
    _error: unknown,
    _variables: UpdateHuggingFaceTokenInput,
    context?: HuggingFaceTokensMutationContext,
  ) => {
    if (context?.previousTokens) {
      activeQueryClient.setQueryData(huggingFaceTokensQueryKey, context.previousTokens)
    }
  },
  onSettled: async () => {
    await refreshHuggingFaceTokens(activeQueryClient)
  },
})

const createDeleteHuggingFaceTokenMutationOptions = (activeQueryClient: QueryClient) => ({
  mutationFn: deleteHuggingFaceToken,
  onMutate: async (id: number): Promise<HuggingFaceTokensMutationContext> => {
    await activeQueryClient.cancelQueries({ queryKey: huggingFaceTokensQueryKey })
    const previousTokens =
      activeQueryClient.getQueryData<HuggingFaceToken[]>(huggingFaceTokensQueryKey)
    activeQueryClient.setQueryData<HuggingFaceToken[]>(
      huggingFaceTokensQueryKey,
      (previousTokens ?? []).filter((savedToken) => savedToken.id !== id),
    )
    return { previousTokens }
  },
  onError: (_error: unknown, _variables: number, context?: HuggingFaceTokensMutationContext) => {
    if (context?.previousTokens) {
      activeQueryClient.setQueryData(huggingFaceTokensQueryKey, context.previousTokens)
    }
  },
  onSettled: async () => {
    await refreshHuggingFaceTokens(activeQueryClient)
  },
})

export type { HuggingFaceToken, SaveHuggingFaceTokenMutation }
export {
  buildSaveHuggingFaceTokenInput,
  createCreateHuggingFaceTokenMutationOptions,
  createDeleteHuggingFaceTokenMutationOptions,
  createHuggingFaceToken,
  createSaveHuggingFaceTokenMutation,
  createUpdateHuggingFaceTokenMutationOptions,
  deleteHuggingFaceToken,
  huggingFaceTokensQuery,
  huggingFaceTokensQueryKey,
  loadHuggingFaceTokens,
  persistSavedHuggingFaceToken,
  refreshHuggingFaceTokens,
  saveHuggingFaceTokenIfNeeded,
  updateHuggingFaceToken,
}
